import React from 'react';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useCases } from '../../contexts/api-contexts/CasesContext';

interface CaseStatusIndicatorProps {
  showLabel?: boolean;
}

/**
 * Component that displays the current case status with a spinner for loading states
 */
const CaseStatusIndicator: React.FC<CaseStatusIndicatorProps> = ({
  showLabel = true,
}) => {
  const { currentCase, isLoading, isSaving, isRefreshing } = useCases();

  // Handle different states and statuses
  const getStatusDisplay = (): {
    label: string;
    color: string;
    loading: boolean;
  } => {
    // Show loading/saving states first
    if (isSaving) {
      return { label: 'Saving...', color: '#f57c00', loading: true }; // Orange
    }

    if (isLoading) {
      return { label: 'Loading...', color: '#1976d2', loading: true }; // Blue
    }

    if (isRefreshing) {
      return { label: 'Refreshing...', color: '#2196f3', loading: true }; // Light blue
    }

    // If we have a current case, show its status
    if (currentCase?.status) {
      switch (currentCase.status) {
        case 'QUEUED':
          return { label: 'Queued', color: '#9e9e9e', loading: true }; // Grey
        case 'PENDING':
          return { label: 'Pending', color: '#f9a825', loading: true }; // Dark yellow
        case 'PROCESSING':
          return { label: 'Processing', color: '#0288d1', loading: true }; // Cyan
        case 'INITIAL_AI_PROCESSING_DONE':
          return {
            label: 'Initial AI Processing Complete',
            color: '#00897b',
            loading: false,
          }; // Teal
        case 'SANITIZING':
          return { label: 'Sanitizing', color: '#7b1fa2', loading: true }; // Purple
        case 'CALCULATING_DIAGNOSES':
          return {
            label: 'Calculating Diagnoses',
            color: '#ff6f00',
            loading: true,
          }; // Orange
        case 'COMPLETED':
          return { label: 'Completed', color: '#43a047', loading: false }; // Green
        case 'ERROR':
          return { label: 'Error', color: '#d32f2f', loading: false }; // Red
        default:
          return { label: 'Unknown Status', color: '#757575', loading: false }; // Grey
      }
    }

    // No case or status
    return { label: 'No Case', color: '#757575', loading: false }; // Grey
  };

  const { label, color, loading } = getStatusDisplay();

  // Don't render anything if no case is loaded or if case status is COMPLETED
  if ((!currentCase && !isLoading) || currentCase?.status === 'COMPLETED') {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        margin: 1,
        padding: 1,
        borderRadius: 1,
        backgroundColor: 'rgba(255,255,255,0.9)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
      }}
    >
      {loading && (
        <CircularProgress
          size={24}
          sx={{
            color,
            marginRight: showLabel ? 1 : 0,
          }}
        />
      )}
      {!loading && (
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: color,
            marginRight: showLabel ? 1 : 0,
          }}
        />
      )}
      {showLabel && (
        <Typography
          variant="body2"
          sx={{
            color,
            fontWeight: 'medium',
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
};

export default CaseStatusIndicator;
